import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Globe, Plus, Save, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const BLANK = { code: "", country: "", phone_digits: "", active: true, is_default: false };

export default function CountryCodesManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(BLANK);

  const load = () => {
    setLoading(true);
    api.get("/country-codes", { params: { include_inactive: true } })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const patchLocal = (id, field, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const save = async (row) => {
    if (!row.code?.trim() || !row.country?.trim()) return toast.error("Code and country are required");
    setSavingId(row.id);
    try {
      await api.patch(`/country-codes/${row.id}`, {
        code: row.code.trim(),
        country: row.country.trim(),
        phone_digits: row.phone_digits ? Number(row.phone_digits) : null,
        active: !!row.active,
        is_default: !!row.is_default,
      });
      toast.success(`${row.code} saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.code} ${row.country}?`)) return;
    try {
      await api.delete(`/country-codes/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const add = async () => {
    if (!newRow.code?.trim() || !newRow.country?.trim()) return toast.error("Code and country are required");
    setAdding(true);
    try {
      await api.post("/country-codes", {
        code: newRow.code.trim(),
        country: newRow.country.trim(),
        phone_digits: newRow.phone_digits ? Number(newRow.phone_digits) : null,
        active: true,
        is_default: false,
      });
      toast.success("Country code added");
      setNewRow(BLANK);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="country-codes-manager">
      <div className="flex items-center gap-2 mb-1">
        <Globe className="w-4 h-4 text-indigo-600" />
        <h3 className="font-heading text-base font-semibold text-slate-800">Country Codes &amp; Phone Rules</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Set the exact <strong>required digits</strong> per dialing code. Contact numbers are validated against this everywhere they are saved. Leave blank to skip validation for that code.
      </p>

      {/* Add new */}
      <div className="grid grid-cols-12 gap-2 items-end bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
        <div className="col-span-3">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Code</label>
          <Input value={newRow.code} onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
                 placeholder="+974" className="h-9 font-mono mt-1" data-testid="cc-new-code" />
        </div>
        <div className="col-span-5">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Country</label>
          <Input value={newRow.country} onChange={(e) => setNewRow({ ...newRow, country: e.target.value })}
                 placeholder="Qatar" className="h-9 mt-1" data-testid="cc-new-country" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Req. digits</label>
          <Input type="number" min={4} max={15} value={newRow.phone_digits}
                 onChange={(e) => setNewRow({ ...newRow, phone_digits: e.target.value })}
                 placeholder="8" className="h-9 mt-1" data-testid="cc-new-digits" />
        </div>
        <div className="col-span-2">
          <Button onClick={add} disabled={adding} className="w-full h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="cc-add-btn">
            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Add</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[110px]">Code</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="w-[120px]">Req. digits</TableHead>
                <TableHead className="w-[90px]">Active</TableHead>
                <TableHead className="w-[80px]">Default</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`cc-row-${r.code}`}>
                  <TableCell>
                    <Input value={r.code} onChange={(e) => patchLocal(r.id, "code", e.target.value)}
                           className="h-8 font-mono text-xs" />
                  </TableCell>
                  <TableCell>
                    <Input value={r.country} onChange={(e) => patchLocal(r.id, "country", e.target.value)} className="h-8 text-sm" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min={4} max={15} value={r.phone_digits ?? ""}
                           onChange={(e) => patchLocal(r.id, "phone_digits", e.target.value)}
                           placeholder="—" className="h-8 text-sm w-20" data-testid={`cc-digits-${r.code}`} />
                  </TableCell>
                  <TableCell>
                    <Switch checked={!!r.active} onCheckedChange={(v) => patchLocal(r.id, "active", v)} />
                  </TableCell>
                  <TableCell>
                    {r.is_default ? (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        <Star className="w-3 h-3 mr-1" />Default
                      </Badge>
                    ) : (
                      <button type="button" onClick={() => patchLocal(r.id, "is_default", true)}
                              className="text-[11px] text-slate-400 hover:text-indigo-600 underline">Set</button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => save(r)} disabled={savingId === r.id}
                              className="h-8 px-2" data-testid={`cc-save-${r.code}`}>
                        {savingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)} className="h-8 px-2 text-rose-500 hover:text-rose-700">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
